import React, { useState } from "react";
import { Share2, Info } from "lucide-react";
import "./GameResultModal.css";
import CompetitionLeaderboard from "./CompetitionLeaderboard";
import AnimatedScore from "./AnimatedScore";
import FollowPopup from "./FollowPopup";

function GameResultModal({
  title,
  message,
  onClose,
  onShare,
  onSecondaryAction,
  color,
  summary,
  shareLabel,
  secondaryLabel,
  isSecondaryDisabled = false,
  footnote,
  isLoggedIn,
  competition,
  oldScore = null,
  lang = "en",
  onFollow,
  currentUser = null,
  isTutorial = false,
}) {
  const [showFollowPopup, setShowFollowPopup] = useState(false);
  const primaryMetric = summary?.rows?.[0];
  const secondaryMetrics = summary?.rows?.slice(1) ?? [];

  const styleVars = {
    "--result-border-color": color,
    "--result-accent-color": color,
  };

  const renderedFootnote = Array.isArray(footnote)
    ? footnote.filter(Boolean)
    : String(footnote || "")
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);

  return (
    <>
      <div className="result-overlay" onClick={(e) => {
        // Only handle clicks directly on the overlay (not on modal content)
        if (e.target !== e.currentTarget) {
          return;
        }
        
        // Get all overlays in the document
        const allOverlays = Array.from(document.querySelectorAll('[class*="-overlay"]'));
        
        // Find the overlay with the highest z-index (excluding overlays with pointer-events: none)
        let topmostOverlay = null;
        let topmostZIndex = -1;
        allOverlays.forEach(overlay => {
          const styles = window.getComputedStyle(overlay);
          const zIndex = parseInt(styles.zIndex, 10) || 0;
          const pointerEvents = styles.pointerEvents;
          
          // Skip overlays with pointer-events: none (like confetti)
          if (pointerEvents === 'none') {
            return;
          }
          
          if (zIndex > topmostZIndex) {
            topmostZIndex = zIndex;
            topmostOverlay = overlay;
          }
        });
        
        // If this is the topmost overlay, close it
        // If this is NOT the topmost, close the topmost instead
        if (topmostOverlay === e.currentTarget) {
          e.stopPropagation();
          onClose();
        } else if (topmostOverlay) {
          // This is not the topmost, so close the topmost modal
          e.stopPropagation();
          // Find the modal component that owns the topmost overlay and close it
          // We'll trigger a click on the topmost overlay to close it
          topmostOverlay.click();
        }
      }} />
      <div
        className="result-modal"
        style={styleVars}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="result-close-button"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="result-title" style={{ color }}>
          {title}
        </div>
        <div className="result-message">{message}</div>

        {isTutorial && (
          <div className="result-actions" style={{ marginTop: '24px' }}>
            <button
              type="button"
              className="result-ok-button"
              style={{ width: '100%', maxWidth: '180px', margin: '0 auto 24px', display: 'block' }}
              onClick={onClose}
            >
              {lang === "nl" ? "OK" : "OK"}
            </button>
          </div>
        )}

        {!isTutorial && summary && (
          <>
            <div className="result-divider" />
            <div className="result-summary-header">
              <button
                type="button"
                className="result-score-info"
                onClick={summary.onInfo}
                aria-label={summary.infoLabel}
              >
                <Info size={20} strokeWidth={2.3} />
              </button>
            </div>
            {primaryMetric && (
              <div className="result-score-main">
                <span className="result-score-main-value">
                  {oldScore !== null ? (
                    <AnimatedScore
                      value={primaryMetric.value}
                      startValue={oldScore}
                      duration={1500}
                    />
                  ) : (
                    primaryMetric.value
                  )}
                </span>
                <span className="result-score-main-label">
                  {primaryMetric.label}
                </span>
              </div>
            )}
            {secondaryMetrics.length > 0 && (
              <div className="result-score-secondary-row">
                {secondaryMetrics.map((metric, index) => (
                  <div
                    key={`${metric.label}-${index}`}
                    className="result-score-secondary-item"
                  >
                    <span className="result-score-secondary-value">
                      {metric.value}
                    </span>
                    <span className="result-score-secondary-label">
                      {metric.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {!isTutorial && (
          <>
        <div className="result-divider" />
        {competition && (
          <CompetitionLeaderboard
            entries={competition.entries}
            oppositeMonthTop3={competition.oppositeMonthTop3 || []}
            loading={competition.loading}
            error={competition.error}
            monthLabel={competition.monthLabel}
            texts={competition.texts}
            tableLabels={competition.tableLabels}
            onRetry={competition.onRetry}
            profileDefault={competition.profileDefault}
            variant="result"
            active={competition.active}
            disabled={competition.disabled}
            overlayMessage={competition.overlayMessage}
            onClick={competition.onClick}
            lang={competition.lang || "en"}
            onFollow={onFollow}
            currentUser={currentUser}
            refreshLeaderboard={competition?.onRetry}
          />
        )}
        {renderedFootnote.length > 0 && (
          <div className="result-footnote">
            {renderedFootnote.map((line, index) => (
              <span key={index}>{line}</span>
            ))}
          </div>
        )}

        <div className="result-actions">
          <button
            type="button"
            className={`result-secondary-button${
              isLoggedIn ? " result-secondary-button--ok" : ""
            }`}
            onClick={() => {
              if (isLoggedIn && onFollow) {
                setShowFollowPopup(true);
              } else if (onSecondaryAction) {
                onSecondaryAction();
              }
            }}
            disabled={isSecondaryDisabled}
          >
            {isLoggedIn && onFollow ? (
              <span className="result-secondary-button__label">
                <span className="result-secondary-button__plus">+</span>
                <span>{lang === "nl" ? "volg" : "follow"}</span>
              </span>
            ) : (
              secondaryLabel
            )}
          </button>
          <button
            type="button"
            className="result-share-button"
            onClick={onShare}
          >
            <Share2 size={24} strokeWidth={2.5} />
            <span>{shareLabel}</span>
          </button>
        </div>
          </>
        )}
      </div>

      {onFollow && (
        <FollowPopup
          visible={showFollowPopup}
          onClose={() => setShowFollowPopup(false)}
          onFollow={async (usernameOrEmail) => {
            await onFollow(usernameOrEmail);
            setShowFollowPopup(false);
          }}
          lang={lang}
        />
      )}
    </>
  );
}

export default GameResultModal;

